import { Response, SnaphotReference } from '@/models'
import axios from 'axios'
import CONSTANTS from '@/constants/Config.constants'
import { Chart } from '@/models/chart.model'
import {
  ChartDetails,
  AvailableWidgets,
  DashboardResource,
  ParsedConfiguration,
  QueryResultConfiguration,
  UnparsedDashboardResource,
} from '@/models/dashboard.model'
import { IDashboardService } from '@/services/IDashboardService'
import { AnalyzeTableHeader } from '@/models/table.model'
import { useEnvStore } from '@/stores/envStore'
import { useGivenChartDetails } from '@/composables/useChartDetails'

function parseConfiguration(configuration: string): ParsedConfiguration {
  try {
    return JSON.parse(configuration)
  } catch {
    return {}
  }
}

function serializeDashboardResource(dashboard: DashboardResource): UnparsedDashboardResource {
  return {
    ...dashboard,
    widgets: dashboard.widgets.map((widget) => ({
      ...widget,
      configuration: JSON.stringify(widget.configuration),
    })),
  }
}

function deserializeDashboardResource(dashboard: UnparsedDashboardResource): DashboardResource {
  return {
    ...dashboard,
    widgets: dashboard.widgets.map((widget) => ({
      ...widget,
      configuration: parseConfiguration(widget.configuration),
    })),
  }
}

export default class DashboardService implements IDashboardService {
  private get API_URL(): string {
    return useEnvStore().variables.ANALYZE_API_BACKEND_URL
  }

  private axiosConfig = {
    headers: {
      'Content-Type': CONSTANTS.CONTENT_TYPES.YT_V1,
    },
    data: null,
  }

  public async getAvailableWidgets(
    projectId: number,
    snapshotId: SnaphotReference,
    filter?: number,
  ): Promise<AvailableWidgets> {
    const params = { filter }
    return axios
      .get<Response<AvailableWidgets>>(
        `${this.API_URL}/project/${projectId}/snapshot/${snapshotId}/dashboard/widgets`,
        { ...this.axiosConfig, params },
      )
      .then((response) => response.data.data)
      .catch((error) => Promise.reject(error))
  }

  public async createDashboard(
    projectId: number,
    dashboard: Omit<DashboardResource, 'id'>,
  ): Promise<DashboardResource> {
    const data = serializeDashboardResource({ ...dashboard, id: Number.MAX_SAFE_INTEGER })

    return axios
      .post<Response<DashboardResource>>(
        `${this.API_URL}/project/${projectId}/dashboard`,
        { data },
        this.axiosConfig,
      )
      .then((response) => response.data.data)
      .catch((error) => Promise.reject(error))
  }

  public async getDashboards(projectId: number): Promise<DashboardResource[]> {
    return axios
      .get<Response<UnparsedDashboardResource[]>>(
        `${this.API_URL}/project/${projectId}/dashboard`,
        this.axiosConfig,
      )
      .then((response) => response.data.data.map(deserializeDashboardResource))
      .catch((error) => Promise.reject(error))
  }

  public async getDashboard(
    projectId: number,
    dashboardId: number | undefined,
  ): Promise<DashboardResource> {
    return axios
      .get<Response<UnparsedDashboardResource>>(
        `${this.API_URL}/project/${projectId}/dashboard/${dashboardId ?? 0}`,
        this.axiosConfig,
      )
      .then((response) => deserializeDashboardResource(response.data.data))
      .catch((error) => Promise.reject(error))
  }

  public async updateDashboard(
    projectId: number,
    dashboard: DashboardResource,
  ): Promise<DashboardResource> {
    const data = serializeDashboardResource(dashboard)

    return axios
      .put<Response<UnparsedDashboardResource>>(
        `${this.API_URL}/project/${projectId}/dashboard/${dashboard.id}`,
        { data },
        this.axiosConfig,
      )
      .then((response) => deserializeDashboardResource(response.data.data))
      .catch((error) => Promise.reject(error))
  }

  public async deleteDashboard(projectId: number, dashboardId: number): Promise<DashboardResource> {
    return axios
      .delete<Response<UnparsedDashboardResource>>(
        `${this.API_URL}/project/${projectId}/dashboard/${dashboardId}`,
        this.axiosConfig,
      )

      .then((response) => deserializeDashboardResource(response.data.data))
      .catch((error) => Promise.reject(error))
  }

  public async getQueryResultRawData(chart: Chart): Promise<ChartDetails> {
    return axios
      .post<Response<ChartDetails>>(`${this.API_URL}${chart.path}/data`, {}, this.axiosConfig)
      .then((response) => response.data.data)
      .catch((error) => Promise.reject(error))
  }

  public async getAggregatedQueryResult(
    chart: Chart,
    config: QueryResultConfiguration,
  ): Promise<ChartDetails> {
    const visibleAggregations = config.aggregations.filter((agg) => agg.visible !== false)
    const body = {
      groupByColumnId: config.groupByColumnId,
      aggregations: visibleAggregations.map((agg) => ({
        columnId: agg.columnId,
        aggregation: agg.aggregation,
        label: agg.label,
        distinctColumnId: agg.distinctColumnId,
      })),
    }
    return axios
      .post<Response<ChartDetails>>(`${this.API_URL}${chart.path}/data`, body, this.axiosConfig)
      .then((response) => response.data.data)
      .catch((error) => Promise.reject(error))
  }

  public async getChartDetailsData(
    chart: Chart,
    kind: string,
    groupByColumnId: string,
    serverOptions: { page: number; rowsPerPage: number },
  ): Promise<ChartDetails> {
    const params = {
      page: serverOptions.page,
      pageSize: serverOptions.rowsPerPage,
    }

    if (chart.detailsPath) {
      return axios
        .get<Response<any>>(`${this.API_URL}${chart.detailsPath}`, {
          ...this.axiosConfig,
          params: { ...params, kind },
        })
        .then((response) => response.data.data)
        .catch((error) => Promise.reject(error))
    }

    const body = {
      groupByColumnId,
      havingValue: kind,
    }

    return axios
      .post<Response<any>>(`${this.API_URL}${chart.path}/data`, body, {
        ...this.axiosConfig,
        params,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        return Promise.reject(error)
      })
  }

  public getChartDetailsHeaders(chartDetails: ChartDetails): AnalyzeTableHeader[] {
    return useGivenChartDetails(chartDetails).headers.value
  }

  public getChartDetailsItems(chartDetails: ChartDetails): any[] {
    return useGivenChartDetails(chartDetails).items.value
  }

  public getChartDetailsLinks(chartDetails: ChartDetails): Record<number, Record<string, string>> {
    return useGivenChartDetails(chartDetails).links.value
  }

  public getMetricsDetailsPath(chart: Chart): string | undefined {
    const pathSource = chart.detailsPath ?? chart.path
    return pathSource?.split('dashboard/')?.at(1)
  }
}
